import React, { forwardRef } from 'react'
import { Pressable, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Plus } from '../../icons/actions'
import { Icon } from '../../icons/Icon'
import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { buttonAttachmentContract } from './button-attachment.contract'

const CELL = buttonAttachmentContract.variants.default

export const ATTACHMENT_LABEL_VARIANT = variantForStyleId(buttonAttachmentContract.textStyleIds[0])
export const ATTACHMENT_HINT_VARIANT = variantForStyleId(buttonAttachmentContract.textStyleIds[1])

export type ButtonAttachmentProps = Omit<
    React.ComponentProps<typeof Pressable>,
    'style' | 'children'
> & {
    children: string
    /**
     * Figma's `showFormatHint`, the second line under the label. It is a
     * boolean there and a string here for the same reason `showLabel` is on
     * `TextAreaInput`: the component draws the hint when it has one to draw.
     */
    formatHint?: string
    className?: string
}

type AttachmentRef = React.ComponentRef<typeof Pressable>

/**
 * The tile that opens a file picker: a plus, a label beside it, and an optional
 * line of accepted formats underneath.
 *
 * Its two texts are the reason `parts` exists. They disagree — the label is
 * `content/default` in one style, the hint `content/muted` in another — and the
 * matrix has one `label` field, so the dumper used to refuse the component
 * outright rather than pick a winner. Each now carries its own row.
 *
 * The 124x124 Figma draws is FIXED on both axes, so it stays: this is a tile,
 * and a caller that wants a different one passes `className`.
 */
export const ButtonAttachment = forwardRef<AttachmentRef, ButtonAttachmentProps>(
    ({ children, formatHint, disabled = false, className, ...rest }, ref) => (
        <Pressable
            ref={ref}
            disabled={disabled}
            role='button'
            className={twMerge(
                'flex-col justify-center',
                CELL.container,
                disabled ? CELL.disabled : undefined,
                className,
            )}
            testID='button-attachment'
            {...rest}
        >
            <View className={twMerge('flex-row', CELL.parts['icon-label'])}>
                <Icon glyph={Plus} className={CELL.icon} />
                <Typography
                    variant={ATTACHMENT_LABEL_VARIANT}
                    className={CELL.parts['option-label']}
                >
                    {children}
                </Typography>
            </View>
            {formatHint ? (
                <Typography
                    variant={ATTACHMENT_HINT_VARIANT}
                    className={twMerge('self-stretch', CELL.parts['format-hint'])}
                >
                    {formatHint}
                </Typography>
            ) : null}
        </Pressable>
    ),
)

ButtonAttachment.displayName = 'ButtonAttachment'
